import React, { useState, useEffect, useCallback } from "react";
import { Patient } from "../../PatientCard";
import TreatmentPlanService, {
  TreatmentPlan,
} from "../../../../services/treatmentPlanService";

interface TreatmentPlanHistoryProps {
  patient: Patient;
  isOpen: boolean;
  onClose: () => void;
  refreshTrigger?: number;
}

const TreatmentPlanHistory: React.FC<TreatmentPlanHistoryProps> = ({
  patient,
  isOpen,
  onClose,
  refreshTrigger,
}) => {
  const [treatmentPlans, setTreatmentPlans] = useState<TreatmentPlan[]>([]);
  const [selectedPlan, setSelectedPlan] = useState<TreatmentPlan | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isMobile, setIsMobile] = useState(false);
  const [actionLoading, setActionLoading] = useState<{
    [key: string]: {
      complete?: boolean;
      pause?: boolean;
      resume?: boolean;
      cancel?: boolean;
    };
  }>({});

  // Check for mobile screen size
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };

    checkMobile();
    window.addEventListener("resize", checkMobile);
    return () => window.removeEventListener("resize", checkMobile);
  }, []);

  useEffect(() => {
    if (isOpen && patient.id) {
      loadTreatmentPlans();
    }
  }, [isOpen, patient.id, refreshTrigger]);

  const loadTreatmentPlans = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const result = await TreatmentPlanService.getPatientTreatmentPlans(
        patient.id
      );
      setTreatmentPlans(result);
    } catch (err: any) {
      setError(err.message || "Failed to load treatment plans");
    } finally {
      setIsLoading(false);
    }
  }, [patient.id]);

  useEffect(() => {
    if (isOpen && patient.id) {
      loadTreatmentPlans();
    }
  }, [isOpen, patient.id, refreshTrigger, loadTreatmentPlans]);

  const handleAction = async (
    planId: number,
    action: "complete" | "pause" | "resume" | "cancel"
  ) => {
    setActionLoading((prev) => ({
      ...prev,
      [planId.toString()]: { ...prev[planId.toString()], [action]: true },
    }));

    try {
      let updatedPlan: TreatmentPlan;

      switch (action) {
        case "complete":
          const outcome = prompt("Enter outcome summary (optional):");
          updatedPlan = await TreatmentPlanService.completeTreatmentPlan(
            planId,
            outcome ? { outcome } : undefined
          );
          break;
        case "pause":
          updatedPlan = await TreatmentPlanService.pauseTreatmentPlan(planId);
          break;
        case "resume":
          updatedPlan = await TreatmentPlanService.resumeTreatmentPlan(planId);
          break;
        case "cancel":
          updatedPlan = await TreatmentPlanService.cancelTreatmentPlan(planId);
          break;
        default:
          throw new Error(`Unknown action: ${action}`);
      }

      // Update plan in state
      setTreatmentPlans((prev) =>
        prev.map((plan) => (plan.id === planId ? updatedPlan : plan))
      );

      // Update selected plan if it's the one being modified
      if (selectedPlan?.id === planId) {
        setSelectedPlan(updatedPlan);
      }

      const messages = {
        complete: "Treatment plan completed successfully",
        pause: "Treatment plan paused",
        resume: "Treatment plan resumed",
        cancel: "Treatment plan cancelled",
      };

      alert(messages[action]);
    } catch (err: any) {
      console.error(`Failed to ${action} treatment plan:`, err);
      alert(`Failed to ${action} treatment plan: ${err.message}`);
    } finally {
      setActionLoading((prev) => ({
        ...prev,
        [planId.toString()]: { ...prev[planId.toString()], [action]: false },
      }));
    }
  };

  const handleCompleteItem = async (planId: number, itemId: number) => {
    const notes = prompt("Add completion notes (optional):");

    const loadingKey = `${planId}-${itemId}`;
    setActionLoading((prev) => ({
      ...prev,
      [loadingKey]: { complete: true },
    }));

    try {
      const currentPlan = await TreatmentPlanService.getTreatmentPlan(planId);
      const currentItem = currentPlan.items.find((item) => item.id === itemId);

      const completedItem = await TreatmentPlanService.completeItem(
        planId,
        itemId,
        notes || undefined
      );

      const updatedPlan = await TreatmentPlanService.getTreatmentPlan(planId);

      setTreatmentPlans((prev) =>
        prev.map((plan) => (plan.id === planId ? updatedPlan : plan))
      );

      if (selectedPlan?.id === planId) {
        setSelectedPlan(updatedPlan);
      }

      // alert("Treatment item completed successfully");
    } catch (err: any) {
      console.error("Failed to complete item:", err);

      if (err.message.includes("Cannot complete item in current status")) {
        try {
          const currentPlan = await TreatmentPlanService.getTreatmentPlan(
            planId
          );
          const currentItem = currentPlan.items.find(
            (item) => item.id === itemId
          );
          console.error("Item details:", {
            itemId,
            currentStatus: currentItem?.status,
            planStatus: currentPlan.status,
            canComplete:
              currentItem?.status === "pending" ||
              currentItem?.status === "in_progress",
          });
          alert(
            `Failed to complete item: Item is currently "${currentItem?.status}". Only pending or in-progress items can be completed.`
          );
        } catch (planErr) {
          alert(`Failed to complete item: ${err.message}`);
        }
      } else {
        alert(`Failed to complete item: ${err.message}`);
      }
    } finally {
      setActionLoading((prev) => {
        const newState = { ...prev };
        delete newState[loadingKey];
        return newState;
      });
    }
  };

  if (!isOpen) return null;

  const buttonStyle = (
    variant:
      | "primary"
      | "secondary"
      | "success"
      | "warning"
      | "danger" = "primary"
  ): React.CSSProperties => {
    const variants = {
      primary: { background: "#3b82f6", color: "white" },
      secondary: { background: "#e5e7eb", color: "#374151" },
      success: { background: "#10b981", color: "white" },
      warning: { background: "#f59e0b", color: "white" },
      danger: { background: "#ef4444", color: "white" },
    };

    return {
      padding: "8px 12px",
      borderRadius: "6px",
      border: "none",
      fontSize: "12px",
      fontWeight: "500",
      cursor: "pointer",
      transition: "all 0.2s ease",
      ...variants[variant],
      minWidth: isMobile ? "auto" : "80px",
      textAlign: "center",
    };
  };

  return (
    <>
      <div
        style={{
          background: "#ffffff",
          borderRadius: "12px",
          padding: isMobile ? "16px" : "20px",
          marginBottom: "24px",
          boxShadow: "0 1px 3px rgba(0, 0, 0, 0.1)",
          border: "1px solid #e5e7eb",
          minHeight: isMobile ? "auto" : "600px",
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
        }}
      >
        {/* Header */}
        <div
          style={{
            padding: "0 0 16px 0",
            borderBottom: "1px solid #e5e7eb",
            marginBottom: "16px",
          }}
        >
          <h2
            style={{
              fontSize: isMobile ? "18px" : "20px",
              fontWeight: "600",
              margin: 0,
              marginBottom: "4px",
            }}
          >
            Treatment Plan History
          </h2>
          <p style={{ fontSize: "14px", color: "#64748b", margin: 0 }}>
            All treatment plans for{" "}
            {patient.full_name || `${patient.first_name} ${patient.last_name}`}
          </p>
        </div>

        {/* Main Content */}
        <div
          style={{
            flex: 1,
            display: "flex",
            flexDirection: isMobile ? "column" : "row",
            overflow: "hidden",
            gap: isMobile ? "20px" : "0",
            minHeight: 0,
          }}
        >
          {/* Sidebar - Treatment Plans List */}
          <div
            style={{
              width: isMobile ? "100%" : selectedPlan ? "40%" : "100%",
              borderRight: isMobile
                ? "none"
                : selectedPlan
                ? "1px solid #e5e7eb"
                : "none",
              borderBottom:
                isMobile && selectedPlan ? "1px solid #e5e7eb" : "none",
              overflow: "auto",
              paddingRight: isMobile ? "0" : selectedPlan ? "16px" : "0",
              paddingBottom: isMobile && selectedPlan ? "16px" : "0",
              transition: "width 0.3s ease",
              minHeight: 0,
            }}
          >
            {isLoading ? (
              <div
                style={{
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  height: "200px",
                  flexDirection: "column",
                  gap: "12px",
                }}
              >
                <div
                  style={{
                    width: "40px",
                    height: "40px",
                    border: "4px solid #e2e8f0",
                    borderTop: "4px solid #10b981",
                    borderRadius: "50%",
                    animation: "spin 1s linear infinite",
                  }}
                />
                Loading treatment plans...
              </div>
            ) : error ? (
              <div
                style={{
                  textAlign: "center",
                  color: "#ef4444",
                  padding: "40px",
                  fontSize: "16px",
                }}
              >
                {error}
                <br />
                <button
                  onClick={loadTreatmentPlans}
                  style={{ ...buttonStyle("primary"), marginTop: "16px" }}
                >
                  Retry
                </button>
              </div>
            ) : treatmentPlans.length === 0 ? (
              <div
                style={{
                  textAlign: "center",
                  color: "#6b7280",
                  padding: "40px",
                  fontSize: "16px",
                }}
              >
                No treatment plans found for this patient
              </div>
            ) : (
              <div>
                <h3
                  style={{
                    fontSize: "16px",
                    fontWeight: "600",
                    marginBottom: "16px",
                    color: "#1f2937",
                  }}
                >
                  Treatment Plans ({treatmentPlans.length})
                </h3>

                <div
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    gap: "12px",
                  }}
                >
                  {treatmentPlans.map((plan) => (
                    <div
                      key={plan.id}
                      onClick={() =>
                        setSelectedPlan(
                          selectedPlan?.id === plan.id ? null : plan
                        )
                      }
                      style={{
                        border:
                          selectedPlan?.id === plan.id
                            ? "2px solid #10b981"
                            : "1px solid #e5e7eb",
                        borderRadius: "8px",
                        padding: "16px",
                        cursor: "pointer",
                        transition: "all 0.2s ease",
                        background:
                          selectedPlan?.id === plan.id ? "#f0fdf4" : "#ffffff",
                      }}
                      onMouseEnter={(e) => {
                        if (selectedPlan?.id !== plan.id) {
                          e.currentTarget.style.borderColor = "#10b981";
                          e.currentTarget.style.background = "#f9fafb";
                        }
                      }}
                      onMouseLeave={(e) => {
                        if (selectedPlan?.id !== plan.id) {
                          e.currentTarget.style.borderColor = "#e5e7eb";
                          e.currentTarget.style.background = "#ffffff";
                        }
                      }}
                    >
                      <div
                        style={{
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "flex-start",
                          marginBottom: "8px",
                          flexWrap: "wrap",
                          gap: "8px",
                        }}
                      >
                        <h4
                          style={{
                            fontSize: "14px",
                            fontWeight: "600",
                            margin: 0,
                            color: "#1f2937",
                            flex: 1,
                            minWidth: 0,
                          }}
                        >
                          {TreatmentPlanService.getStatusIcon(plan.status)}{" "}
                          {plan.plan_name}
                        </h4>
                        <div
                          style={{
                            display: "flex",
                            flexWrap: "wrap",
                            gap: "4px",
                          }}
                        >
                          <span
                            style={{
                              padding: "2px 6px",
                              borderRadius: "8px",
                              fontSize: "10px",
                              fontWeight: "500",
                              background:
                                TreatmentPlanService.getStatusColor(
                                  plan.status
                                ) + "20",
                              color: TreatmentPlanService.getStatusColor(
                                plan.status
                              ),
                            }}
                          >
                            {TreatmentPlanService.getStatusLabel(plan.status)}
                          </span>
                          <span
                            style={{
                              padding: "2px 6px",
                              borderRadius: "8px",
                              fontSize: "10px",
                              fontWeight: "500",
                              background:
                                TreatmentPlanService.getPriorityColor(
                                  plan.priority
                                ) + "20",
                              color: TreatmentPlanService.getPriorityColor(
                                plan.priority
                              ),
                            }}
                          >
                            {TreatmentPlanService.getPriorityIcon(
                              plan.priority
                            )}{" "}
                            {TreatmentPlanService.getPriorityLabel(
                              plan.priority
                            )}
                          </span>
                        </div>
                      </div>

                      <div
                        style={{
                          fontSize: "12px",
                          color: "#6b7280",
                          marginBottom: "8px",
                          wordBreak: "break-word",
                        }}
                      >
                        {plan.diagnosis}
                      </div>

                      <div
                        style={{
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "center",
                          fontSize: "11px",
                          color: "#9ca3af",
                          flexWrap: "wrap",
                          gap: "8px",
                        }}
                      >
                        <span style={{ wordBreak: "break-word" }}>
                          {TreatmentPlanService.formatDate(plan.start_date)}
                          {plan.end_date &&
                            ` - ${TreatmentPlanService.formatDate(
                              plan.end_date
                            )}`}
                        </span>
                        <span>{plan.total_items_count} items</span>
                      </div>

                      {/* Progress Bar */}
                      <div
                        style={{
                          marginTop: "8px",
                          height: "4px",
                          background: "#e5e7eb",
                          borderRadius: "2px",
                          overflow: "hidden",
                        }}
                      >
                        <div
                          style={{
                            width: `${plan.progress_percentage}%`,
                            height: "100%",
                            background:
                              plan.progress_percentage === 100
                                ? "#10b981"
                                : "#3b82f6",
                            transition: "width 0.3s ease",
                          }}
                        />
                      </div>
                      <div
                        style={{
                          fontSize: "10px",
                          color: "#6b7280",
                          marginTop: "2px",
                          textAlign: "right",
                        }}
                      >
                        {plan.progress_percentage}% Complete (
                        {plan.completed_items_count}/{plan.total_items_count})
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Details Panel */}
          {selectedPlan && (
            <div
              style={{
                width: isMobile ? "100%" : "60%",
                overflow: "auto",
                paddingLeft: isMobile ? "0" : "16px",
                paddingTop: isMobile ? "16px" : "0",
                background: isMobile ? "#ffffff" : "#f8fafc",
                minHeight: 0,
              }}
            >
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "flex-start",
                  marginBottom: "20px",
                  flexWrap: "wrap",
                  gap: "16px",
                }}
              >
                <div style={{ flex: 1, minWidth: 0 }}>
                  <h3
                    style={{
                      fontSize: isMobile ? "18px" : "20px",
                      fontWeight: "600",
                      margin: 0,
                      marginBottom: "8px",
                      color: "#1f2937",
                      wordBreak: "break-word",
                    }}
                  >
                    {selectedPlan.plan_name}
                  </h3>
                  <div
                    style={{
                      display: "flex",
                      gap: "8px",
                      alignItems: "center",
                      marginBottom: "8px",
                      flexWrap: "wrap",
                    }}
                  >
                    <span
                      style={{
                        padding: "4px 8px",
                        borderRadius: "12px",
                        fontSize: "12px",
                        fontWeight: "500",
                        background:
                          TreatmentPlanService.getStatusColor(
                            selectedPlan.status
                          ) + "20",
                        color: TreatmentPlanService.getStatusColor(
                          selectedPlan.status
                        ),
                      }}
                    >
                      {TreatmentPlanService.getStatusIcon(selectedPlan.status)}{" "}
                      {TreatmentPlanService.getStatusLabel(selectedPlan.status)}
                    </span>
                    <span
                      style={{
                        padding: "4px 8px",
                        borderRadius: "12px",
                        fontSize: "12px",
                        fontWeight: "500",
                        background:
                          TreatmentPlanService.getPriorityColor(
                            selectedPlan.priority
                          ) + "20",
                        color: TreatmentPlanService.getPriorityColor(
                          selectedPlan.priority
                        ),
                      }}
                    >
                      {TreatmentPlanService.getPriorityIcon(
                        selectedPlan.priority
                      )}{" "}
                      {TreatmentPlanService.getPriorityLabel(
                        selectedPlan.priority
                      )}
                    </span>
                  </div>
                  <div
                    style={{
                      fontSize: "14px",
                      color: "#6b7280",
                      wordBreak: "break-word",
                    }}
                  >
                    <strong>Diagnosis:</strong> {selectedPlan.diagnosis}
                  </div>
                </div>

                {/* Action Buttons */}
                <div
                  style={{
                    display: "flex",
                    flexDirection: isMobile ? "row" : "column",
                    gap: "8px",
                    flexWrap: "wrap",
                  }}
                >
                  {selectedPlan.status === "active" && (
                    <>
                      <button
                        onClick={() =>
                          handleAction(selectedPlan.id!, "complete")
                        }
                        disabled={
                          actionLoading[selectedPlan.id!.toString()]?.complete
                        }
                        style={{
                          ...buttonStyle("success"),
                          opacity: actionLoading[selectedPlan.id!.toString()]
                            ?.complete
                            ? 0.7
                            : 1,
                        }}
                      >
                        {actionLoading[selectedPlan.id!.toString()]?.complete
                          ? "Completing..."
                          : "Complete"}
                      </button>
                      <button
                        onClick={() => handleAction(selectedPlan.id!, "pause")}
                        disabled={
                          actionLoading[selectedPlan.id!.toString()]?.pause
                        }
                        style={{
                          ...buttonStyle("warning"),
                          opacity: actionLoading[selectedPlan.id!.toString()]
                            ?.pause
                            ? 0.7
                            : 1,
                        }}
                      >
                        {actionLoading[selectedPlan.id!.toString()]?.pause
                          ? "Pausing..."
                          : "Pause"}
                      </button>
                    </>
                  )}

                  {selectedPlan.status === "paused" && (
                    <button
                      onClick={() => handleAction(selectedPlan.id!, "resume")}
                      disabled={
                        actionLoading[selectedPlan.id!.toString()]?.resume
                      }
                      style={{
                        ...buttonStyle("primary"),
                        opacity: actionLoading[selectedPlan.id!.toString()]
                          ?.resume
                          ? 0.7
                          : 1,
                      }}
                    >
                      {actionLoading[selectedPlan.id!.toString()]?.resume
                        ? "Resuming..."
                        : "Resume"}
                    </button>
                  )}

                  {(selectedPlan.status === "active" ||
                    selectedPlan.status === "paused") && (
                    <button
                      onClick={() => {
                        if (
                          window.confirm(
                            "Are you sure you want to cancel this treatment plan?"
                          )
                        ) {
                          handleAction(selectedPlan.id!, "cancel");
                        }
                      }}
                      disabled={
                        actionLoading[selectedPlan.id!.toString()]?.cancel
                      }
                      style={{
                        ...buttonStyle("danger"),
                        opacity: actionLoading[selectedPlan.id!.toString()]
                          ?.cancel
                          ? 0.7
                          : 1,
                      }}
                    >
                      {actionLoading[selectedPlan.id!.toString()]?.cancel
                        ? "Cancelling..."
                        : "Cancel"}
                    </button>
                  )}
                </div>
              </div>

              {/* Plan Overview */}
              <div
                style={{
                  background: "#ffffff",
                  border: "1px solid #e5e7eb",
                  borderRadius: "8px",
                  padding: "16px",
                  marginBottom: "20px",
                }}
              >
                <div
                  style={{
                    display: "grid",
                    gridTemplateColumns: isMobile
                      ? "repeat(2, 1fr)"
                      : "repeat(auto-fit, minmax(100px, 1fr))",
                    gap: "16px",
                    marginBottom: "16px",
                  }}
                >
                  <div>
                    <div
                      style={{
                        fontSize: "20px",
                        fontWeight: "700",
                        color: "#10b981",
                      }}
                    >
                      {selectedPlan.progress_percentage}%
                    </div>
                    <div style={{ fontSize: "12px", color: "#6b7280" }}>
                      Progress
                    </div>
                  </div>
                  <div>
                    <div
                      style={{
                        fontSize: "20px",
                        fontWeight: "700",
                        color: "#3b82f6",
                      }}
                    >
                      {selectedPlan.completed_items_count}
                    </div>
                    <div style={{ fontSize: "12px", color: "#6b7280" }}>
                      Completed
                    </div>
                  </div>
                  <div>
                    <div
                      style={{
                        fontSize: "20px",
                        fontWeight: "700",
                        color: "#f59e0b",
                      }}
                    >
                      {selectedPlan.pending_items_count}
                    </div>
                    <div style={{ fontSize: "12px", color: "#6b7280" }}>
                      Pending
                    </div>
                  </div>
                  <div>
                    <div
                      style={{
                        fontSize: "20px",
                        fontWeight: "700",
                        color: "#ef4444",
                      }}
                    >
                      {selectedPlan.critical_items_count}
                    </div>
                    <div style={{ fontSize: "12px", color: "#6b7280" }}>
                      Critical
                    </div>
                  </div>
                </div>

                {selectedPlan.objectives && (
                  <div
                    style={{
                      padding: "12px",
                      background: "#f0f9ff",
                      border: "1px solid #dbeafe",
                      borderRadius: "6px",
                      marginBottom: "12px",
                    }}
                  >
                    <div
                      style={{
                        fontSize: "12px",
                        fontWeight: "600",
                        color: "#1e40af",
                        marginBottom: "4px",
                      }}
                    >
                      Objectives:
                    </div>
                    <div
                      style={{
                        fontSize: "13px",
                        color: "#1e40af",
                        wordBreak: "break-word",
                      }}
                    >
                      {selectedPlan.objectives}
                    </div>
                  </div>
                )}

                <div
                  style={{
                    fontSize: "12px",
                    color: "#6b7280",
                    wordBreak: "break-word",
                  }}
                >
                  <strong>Duration:</strong>{" "}
                  {TreatmentPlanService.formatDate(selectedPlan.start_date)}
                  {selectedPlan.end_date &&
                    ` - ${TreatmentPlanService.formatDate(
                      selectedPlan.end_date
                    )}`}
                </div>
              </div>

              {/* Treatment Items */}
              <div
                style={{
                  background: "#ffffff",
                  border: "1px solid #e5e7eb",
                  borderRadius: "8px",
                  padding: "16px",
                }}
              >
                <h4
                  style={{
                    fontSize: "16px",
                    fontWeight: "600",
                    marginBottom: "16px",
                    color: "#1f2937",
                  }}
                >
                  Treatment Items ({selectedPlan.items.length})
                </h4>

                <div
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    gap: "12px",
                  }}
                >
                  {selectedPlan.items.map((item, index) => (
                    <div
                      key={index}
                      style={{
                        border: "1px solid #f3f4f6",
                        borderRadius: "6px",
                        padding: "12px",
                        background: "#fafafa",
                      }}
                    >
                      <div
                        style={{
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "flex-start",
                          marginBottom: "8px",
                          flexWrap: "wrap",
                          gap: "8px",
                        }}
                      >
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div
                            style={{
                              display: "flex",
                              alignItems: "center",
                              gap: "8px",
                              marginBottom: "4px",
                              flexWrap: "wrap",
                            }}
                          >
                            <span
                              style={{
                                padding: "2px 6px",
                                borderRadius: "8px",
                                fontSize: "10px",
                                fontWeight: "500",
                                background:
                                  TreatmentPlanService.getItemTypeColor(
                                    item.item_type
                                  ) + "20",
                                color: TreatmentPlanService.getItemTypeColor(
                                  item.item_type
                                ),
                              }}
                            >
                              {TreatmentPlanService.getItemTypeIcon(
                                item.item_type
                              )}{" "}
                              {TreatmentPlanService.getItemTypeLabel(
                                item.item_type
                              )}
                            </span>
                            <span
                              style={{
                                padding: "2px 6px",
                                borderRadius: "8px",
                                fontSize: "10px",
                                fontWeight: "500",
                                background:
                                  TreatmentPlanService.getItemStatusColor(
                                    item.status
                                  ) + "20",
                                color: TreatmentPlanService.getItemStatusColor(
                                  item.status
                                ),
                              }}
                            >
                              {TreatmentPlanService.getItemStatusIcon(
                                item.status
                              )}{" "}
                              {TreatmentPlanService.getItemStatusLabel(
                                item.status
                              )}
                            </span>
                            {item.is_critical && (
                              <span
                                style={{
                                  padding: "2px 6px",
                                  borderRadius: "8px",
                                  fontSize: "10px",
                                  fontWeight: "500",
                                  background: "#fee2e2",
                                  color: "#dc2626",
                                }}
                              >
                                Critical
                              </span>
                            )}
                          </div>
                          <h5
                            style={{
                              fontSize: "14px",
                              fontWeight: "600",
                              margin: "0 0 4px 0",
                              color: "#1f2937",
                              wordBreak: "break-word",
                            }}
                          >
                            #{item.order_sequence} {item.title}
                          </h5>
                          {item.description && (
                            <p
                              style={{
                                fontSize: "12px",
                                color: "#6b7280",
                                margin: "0 0 4px 0",
                                wordBreak: "break-word",
                              }}
                            >
                              {item.description}
                            </p>
                          )}
                          <div
                            style={{
                              fontSize: "11px",
                              color: "#9ca3af",
                              display: "flex",
                              flexWrap: "wrap",
                              gap: "8px",
                            }}
                          >
                            {item.frequency && <span>{item.frequency}</span>}
                            {item.duration && <span>{item.duration}</span>}
                            {item.start_date && (
                              <span>
                                {TreatmentPlanService.formatDate(
                                  item.start_date
                                )}
                              </span>
                            )}
                          </div>
                        </div>

                        {/* Item Actions */}
                        {item.status === "pending" &&
                          selectedPlan.status === "active" &&
                          item.id && (
                            <button
                              onClick={() => {
                                handleCompleteItem(selectedPlan.id!, item.id!);
                              }}
                              style={{
                                ...buttonStyle("success"),
                                marginLeft: isMobile ? "0" : "12px",
                                marginTop: isMobile ? "8px" : "0",
                              }}
                            >
                              Complete
                            </button>
                          )}
                      </div>

                      {item.instructions && (
                        <div
                          style={{
                            background: "#eff6ff",
                            border: "1px solid #dbeafe",
                            borderRadius: "4px",
                            padding: "8px",
                            fontSize: "11px",
                            color: "#1e40af",
                            marginTop: "8px",
                            wordBreak: "break-word",
                          }}
                        >
                          <strong>Instructions:</strong> {item.instructions}
                        </div>
                      )}

                      {item.completion_notes && (
                        <div
                          style={{
                            background: "#f0fdf4",
                            border: "1px solid #bbf7d0",
                            borderRadius: "4px",
                            padding: "8px",
                            fontSize: "11px",
                            color: "#166534",
                            marginTop: "8px",
                            wordBreak: "break-word",
                          }}
                        >
                          <strong>Completion Notes:</strong>{" "}
                          {item.completion_notes}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>

              {/* Progress Notes */}
              {selectedPlan.progress_notes && (
                <div
                  style={{
                    background: "#ffffff",
                    border: "1px solid #e5e7eb",
                    borderRadius: "8px",
                    padding: "16px",
                    marginTop: "20px",
                  }}
                >
                  <h4
                    style={{
                      fontSize: "14px",
                      fontWeight: "600",
                      marginBottom: "8px",
                      color: "#1f2937",
                    }}
                  >
                    Progress Notes
                  </h4>
                  <p
                    style={{
                      fontSize: "13px",
                      color: "#4b5563",
                      margin: 0,
                      lineHeight: "1.5",
                      wordBreak: "break-word",
                    }}
                  >
                    {selectedPlan.progress_notes}
                  </p>
                </div>
              )}

              {/* Outcome */}
              {selectedPlan.outcome && (
                <div
                  style={{
                    background: "#f0fdf4",
                    border: "1px solid #bbf7d0",
                    borderRadius: "8px",
                    padding: "16px",
                    marginTop: "20px",
                  }}
                >
                  <h4
                    style={{
                      fontSize: "14px",
                      fontWeight: "600",
                      marginBottom: "8px",
                      color: "#166534",
                    }}
                  >
                    Treatment Outcome
                  </h4>
                  <p
                    style={{
                      fontSize: "13px",
                      color: "#166534",
                      margin: 0,
                      lineHeight: "1.5",
                      wordBreak: "break-word",
                    }}
                  >
                    {selectedPlan.outcome}
                  </p>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      <style>
        {`
         @keyframes spin {
           0% { transform: rotate(0deg); }
           100% { transform: rotate(360deg); }
         }
         
         button:hover:not(:disabled) {
           transform: translateY(-1px);
           box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
         }
         
         @media (max-width: 768px) {
           .mobile-stack {
             flex-direction: column !important;
           }
           
           .mobile-full-width {
             width: 100% !important;
           }
           
           .mobile-hide-border {
             border-right: none !important;
           }
         }
       `}
      </style>
    </>
  );
};

export default TreatmentPlanHistory;
